import {
	ATTRIBUTES,
	publish,
	PUB_SUB_EVENTS,
	SELECTORS,
    createATCitems,
    fetchConfig
} from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import {
	addItemsToCart,
	getCartAttributesFromFormData,
	getDOMCartSectionApiIds,
	renderResponseToCartDrawer
} from '@/scripts/core/cart-functions'

export class ProductPersonalization extends UcoastEl {
    static htmlSelector = 'product-personalization'
    private static readonly ALLOWED_CHARACTERS_PATTERN = /[A-Z0-9+()*&^%$#@!-]/g
    private static readonly ALLOWED_CHARACTERS_PATTERN_FULL = /^[A-Z0-9+()*&^%$#@!-]+$/
    editButtons: NodeListOf<HTMLButtonElement>
    colorInputs: NodeListOf<HTMLInputElement>
    characterInputs: NodeListOf<HTMLInputElement>
    items: NodeListOf<HTMLElement>
    presetButtons: NodeListOf<HTMLButtonElement>
    iconButtons: NodeListOf<HTMLButtonElement>
    iconsPopups: NodeListOf<HTMLElement>
    closeButton: HTMLButtonElement
    editItems: NodeListOf<HTMLElement>
    nextButton: HTMLButtonElement
    previousButton: HTMLButtonElement
    form: HTMLFormElement
    submitButton: HTMLButtonElement
    submitPrice?: HTMLElement
    submitLabel?: HTMLElement
    error?: boolean

    addNewItem?: HTMLElement
    addRightButton?: HTMLButtonElement
    leftBackButton?: HTMLButtonElement
    errorMessage?: HTMLElement
    characterErrorTimeout?: number
    finalSaleCheckbox?: HTMLInputElement
    finalSaleWrapper?: HTMLElement
    finalSaleError?: HTMLElement
    bodyClickHandler: (event: Event) => void

    constructor() {
        super()
        this.editButtons = q.rl('.edit-button', this)
        this.colorInputs = q.rl('.product-personalization__color-input', this)
        this.characterInputs = q.rl('.character-input', this)
        this.nextButton = q.rs('.button-finish', this)
        this.previousButton = q.rs('.button-back', this)
        
        this.items = q.rl('.product-personalization__item', this)
        this.presetButtons = q.rl('.product-personalization__preset-button', this)
        this.iconButtons = q.rl('.product-personalization__icon', this)
        this.iconsPopups = q.rl('.product-personalization__icons', this)
        this.closeButton = q.rs('.product-personalization__close', this)
        this.editItems = q.rl('.product-personalization__info-edit', this)
        this.form = q.rs('form', this)
        this.submitButton = q.rs('.product-personalization__submit', this)
        this.submitPrice = q.os('.product-personalization__submit-price', this.submitButton)
        this.submitLabel = q.os('.product-personalization__submit-label', this.submitButton)
        this.errorMessage = q.os('.product-personalization__error-message', this)
        this.finalSaleCheckbox = q.os('.product-personalization__final-sale-checkbox', this)
        this.finalSaleWrapper = q.os('.product-personalization__final-sale', this)
        this.finalSaleError = q.os('.product-personalization__final-sale-error', this)

        this.bodyClickHandler = this.onClickBody.bind(this)

        this.editButtons.forEach(editButton => {
            editButton.addEventListener('click', this.onClickEditButton.bind(this))
        })

        this.iconButtons.forEach(iconButton => {
            iconButton.addEventListener('click', this.onClickIconButton.bind(this))
        })

        this.presetButtons.forEach(presetButton => {
            presetButton.addEventListener('click', this.onClickPresetButton.bind(this))
        })

        this.colorInputs.forEach(colorInput => {
            colorInput.addEventListener('change', this.onChangeColor.bind(this))
        })

        this.characterInputs.forEach(characterInput => {
            characterInput.addEventListener('beforeinput', this.onBeforeInputCharacter.bind(this))
            characterInput.addEventListener('input', this.onChangeCharacter.bind(this))
            characterInput.addEventListener('focus', (e: Event) => {
                e.preventDefault()
                const inputField = q.rClosestTarget(e, '.product-personalization__input-field')
                const currentWrapper = q.rClosestTarget(e, '.product-personalization__input-content')
                if(!inputField || !currentWrapper) return
                const inputWrappers = q.rl('.product-personalization__input-content', inputField)
                inputWrappers.forEach(inputWrapper => {
                    inputWrapper.classList.toggle('active', inputWrapper == currentWrapper)
                })
                this.hideCharacterError()
            })
            characterInput.addEventListener('keyup', this.onKeyupCharater.bind(this))
        })

        this.nextButton.addEventListener('click', this.next.bind(this))
        this.previousButton.addEventListener('click', this.previous.bind(this))

        this.closeButton.addEventListener('click', this.close.bind(this))

        document.body.addEventListener('click', this.bodyClickHandler)

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this))

        if(this.querySelector('.none-added-title')) {
            this.addNewItem = q.rs('.none-added-title', this)
            this.addRightButton = q.rs('.button-add-right', this)
            this.leftBackButton = q.rs('.button-back-left', this)

            this.addNewItem.addEventListener('click', this.onClickEditButton.bind(this))
            this.addRightButton.addEventListener('click', this.toggleItem.bind(this))
            this.leftBackButton.addEventListener('click', this.toggleItem.bind(this))
        }

        this.finalSaleCheckbox?.addEventListener('change', () => {
            this.validateFinalSaleAcknowledgement()
        })

        this.applyInitialColors()
        this.applyEditingState()
        this.validateInputs()
    }

    override disconnectedCallback() {
        document.body.removeEventListener('click', this.bodyClickHandler)
    }

    private isEditMode(): boolean {
        return this.dataset.mode === 'edit' && Boolean(this.dataset.editLine)
    }

    private updateSubmitPresentation() {
        const isEditMode = this.isEditMode()
        if (this.finalSaleWrapper) {
            this.finalSaleWrapper.hidden = isEditMode
        }
        if (this.submitPrice) {
            this.submitPrice.hidden = isEditMode
        }
        if (this.submitLabel) {
            this.submitLabel.textContent = isEditMode
                ? this.submitLabel.dataset.saveLabel || this.submitLabel.textContent || ''
                : this.submitLabel.dataset.addLabel || this.submitLabel.textContent || ''
        }
    }

    private setSelectedColor(item: HTMLElement, colorName: string) {
        const normalizedColor = colorName.trim().toLowerCase()
        let matchedInput: HTMLInputElement | null = null
        q.rl<HTMLInputElement>('.product-personalization__color-input', item).forEach((input) => {
            const matches = input.value.trim().toLowerCase() === normalizedColor
            input.checked = matches
            if (matches) matchedInput = input
        })
        const selectedColorEl = q.os('.product-personalization__selected-color', item)
        if (selectedColorEl && matchedInput?.value) {
            selectedColorEl.textContent = matchedInput.value.trim()
        }
    }

    private syncPresetSelection(item: HTMLElement) {
        const personalizationValues = this.getPersonalizationValues(item).map(value => value.trim().toLowerCase())
        const currentPreset = personalizationValues.join('|')
        let matchedPreset = ''

        q.ol<HTMLButtonElement>('.product-personalization__preset-button', item)?.forEach(button => {
            if ((button.dataset.preset ?? '').trim().toLowerCase() === currentPreset) {
                matchedPreset = button.dataset.preset ?? ''
            }
        })

        if (matchedPreset) {
            this.setActivePresetButton(item, matchedPreset)
            return
        }

        this.clearPresetSelection(item)
    }

    private applySummaryToItem(item: HTMLElement, summary: string | undefined) {
        const inputContents = Array.from(q.rl('.product-personalization__input-content', item))
        inputContents.forEach(content => {
            content.classList.remove('active')
            this.clearInputContent(content)
        })

        const summaryValue = (summary ?? '').trim()
        let summaryParts = summaryValue
            .split(',')
            .map(part => part.trim())
            .filter(Boolean)

        if (!summaryValue.includes(',') && summaryValue.includes('TEXT:')) {
            const summaryLines = summaryValue
                .split('\n')
                .map(part => part.trim())
                .filter(Boolean)
            const textLine = summaryLines.find(part => part.toUpperCase().startsWith('TEXT:'))
            const colorLine = summaryLines.find(part => part.toUpperCase().startsWith('COLOR:'))
            const textTokens = textLine
                ? textLine.replace(/^TEXT:\s*/i, '').split('|').map(part => part.trim()).filter(Boolean)
                : []
            const colorValue = colorLine ? colorLine.replace(/^COLOR:\s*/i, '').trim() : ''
            summaryParts = [...textTokens, colorValue].filter(Boolean)
        }

        if (!summaryParts.length) {
            this.clearPresetSelection(item)
            return
        }

        const colorName = summaryParts.pop()
        if (colorName) this.setSelectedColor(item, colorName)

        summaryParts.forEach((token, index) => {
            const content = inputContents[index]
            if (!content) return

            if (token.length === 1) {
                this.setInputContentValue(content, token)
                return
            }

            const resolvedIcon = this.resolveIconSelection(item, token)
            this.setInputContentAsIcon(content, resolvedIcon?.handle || token, resolvedIcon?.url || '')
        })

        this.updateInputTextColors(item)
        this.syncPresetSelection(item)
    }

    private applyEditingState() {
        this.updateSubmitPresentation()
        if (!this.isEditMode()) return

        const leftItem = this.querySelector('.product-personalization__item[data-option="left"]') as HTMLElement | null
        const rightItem = this.querySelector('.product-personalization__item[data-option="right"]') as HTMLElement | null

        if (leftItem) {
            this.applySummaryToItem(leftItem, this.dataset.rightShoe)
        }

        if (rightItem) {
            this.applySummaryToItem(rightItem, this.dataset.leftShoe)
        }

        this.onChangePersonalizationValue()
    }

    private closePopup() {
        this.classList.remove('active')
        if (!this.isEditMode() || this.dataset.returnToCart !== 'true') return

        const cartDrawer = q.os<HTMLElement>('cart-drawer', document)
        if (cartDrawer && 'open' in cartDrawer && typeof (cartDrawer as { open?: () => void }).open === 'function') {
            ;(cartDrawer as { open: () => void }).open()
        }
    }

    private buildPersonalizationProperties(): Record<string, string> {
        const properties: Record<string, string> = {}
        const productHex = this.dataset.productColor || ''
        if (productHex) properties._product_color_hex = productHex

        const leftItem = this.querySelector('.product-personalization__item[data-option="left"]') as HTMLElement | null
        const rightItem = this.querySelector('.product-personalization__item[data-option="right"]') as HTMLElement | null
        const leftColorInput = this.querySelector('[name="left-color"]:checked') as HTMLInputElement | null
        const leftHex = leftColorInput?.dataset.hex || ''
        if (leftHex) properties._monogram_hex = leftHex

        const leftPayload = leftItem ? this.getShoePayload(leftItem) : null
        if (leftPayload) {
            properties['RIGHT SHOE'] = leftPayload.displayText
            if (leftPayload.colorName) properties['RIGHT COLOR'] = leftPayload.colorName
            if (leftPayload.monogramText) properties._monogram_text = leftPayload.monogramText
            if (leftPayload.preview) properties._monogram_preview = leftPayload.preview
            if (leftPayload.iconUrls.length) properties._monogram_icon_urls = leftPayload.iconUrls.join('|')
        }

        const rightPayload = rightItem ? this.getShoePayload(rightItem) : null
        if (rightPayload) {
            properties['LEFT SHOE'] = rightPayload.displayText
            if (rightPayload.colorName) properties['LEFT COLOR'] = rightPayload.colorName
        }

        return properties
    }

    private getShoePayload(item: HTMLElement): {
        rawSummary: string
        displayText: string
        colorName: string | null
        monogramText: string | null
        preview: string | null
        iconUrls: string[]
    } | null {
        const colorInput = q.os<HTMLInputElement>('.product-personalization__color-input:checked', item)
        const colorName = colorInput?.value?.trim() || ''

        const summaryTokens: string[] = []
        const previewTokens: string[] = []
        const textTokens: string[] = []
        const iconUrls: string[] = []

        q.rl('.product-personalization__input-content', item).forEach((contentEl) => {
            const content = contentEl as HTMLElement
            const iconHandle = content.dataset.icon
            const input = q.os<HTMLInputElement>('input', content)

            if (iconHandle) {
                summaryTokens.push(iconHandle)
                const url = content.dataset.iconUrl || ''
                if (url) {
                    previewTokens.push(`icon:${url}`)
                    iconUrls.push(url)
                }
                return
            }

            const value = input?.value?.trim().toUpperCase()
            if (!value) return

            summaryTokens.push(value)
            previewTokens.push(`char:${value}`)
            textTokens.push(value)
        })

        if (!summaryTokens.length) return null

        const rawSummary = [...summaryTokens, colorName].filter(Boolean).join(', ')
        const displayText = summaryTokens.map(token => token.trim().toUpperCase()).join(' | ')

        return {
            rawSummary,
            displayText,
            colorName: colorName ? colorName.toUpperCase() : null,
            monogramText: textTokens.length ? textTokens.join('') : null,
            preview: previewTokens.length ? previewTokens.join('|') : null,
            iconUrls,
        }
    }

    validateInputs(): boolean {
        const activeItem = this.querySelector('.product-personalization__item.active') as HTMLElement | null
        if (!activeItem) return false

        const inputContents = q.rl('.product-personalization__input-content', activeItem)
        let hasValidInput = false

        for (const content of inputContents) {
            const iconHandle = (content as HTMLElement).dataset.icon
            const input = q.os<HTMLInputElement>('input', content)

            if (iconHandle && iconHandle.trim() !== '') {
                hasValidInput = true
                break
            }

            if (input && input.value.trim() !== '' && input.value.trim() !== ' ') {
                hasValidInput = true
                break
            }
        }

        if (hasValidInput) {
            this.nextButton.removeAttribute('aria-disabled')
            this.nextButton.classList.remove('button--error')
            if (this.errorMessage) {
                this.errorMessage.hidden = true
            }
        } else {
            this.nextButton.setAttribute('aria-disabled', 'true')
            this.nextButton.classList.add('button--error')
            if (this.errorMessage) {
                this.errorMessage.hidden = false
            }
        }

        return hasValidInput
    }

	// Create a colored mask element for an icon URL
	private createIconMaskElement(url: string, colorHex: string): HTMLSpanElement {
		const span = document.createElement('span')
		span.setAttribute('data-pp-icon-mask', 'true')
		span.setAttribute('aria-hidden', 'true')
		span.style.backgroundColor = colorHex
		;(span.style as any).webkitMaskImage = `url(${url})`
		;(span.style as any).maskImage = `url(${url})`
		return span
	}

	focusNextInput(currentContent: HTMLElement) {
		const inputWrapper = currentContent.closest('.product-personalization__input-wrapper') as HTMLElement | null
		if (!inputWrapper) return
		const contents = q.rl('.product-personalization__input-content', inputWrapper)
		const index = Array.from(contents).indexOf(currentContent)
		const next = contents[index + 1] as HTMLElement | undefined
		if (!next) return
		contents.forEach(c => {
			c.classList.toggle('active', c === next)
		})
		const nextInput = q.os<HTMLInputElement>('input', next)
		if (nextInput) {
			nextInput.focus()
			nextInput.select()
		}
	}

    private clearInputContent(content: HTMLElement) {
        const input = q.os<HTMLInputElement>('input', content)
        if (input) input.value = ''
        content.dataset.icon = ''
        content.dataset.iconUrl = ''
        const existingMask = content.querySelector('[data-pp-icon-mask]')
        if (existingMask) existingMask.remove()
    }

    private setInputContentValue(content: HTMLElement, value: string) {
        this.clearInputContent(content)
        const input = q.os<HTMLInputElement>('input', content)
        if (!input) return
        input.value = value.toUpperCase()
    }

    private setInputContentAsIcon(content: HTMLElement, iconHandle: string, iconUrl: string) {
        this.clearInputContent(content)
        content.dataset.icon = iconHandle
        content.dataset.iconUrl = iconUrl
        if (!iconUrl) return
        const item = q.rc(content, '.product-personalization__item')
        const monogramHex = this.getSelectedColorHex(item) || '#000'
        const mask = this.createIconMaskElement(iconUrl, monogramHex)
        content.appendChild(mask)
    }

    private resolveIconSelection(item: HTMLElement, iconHandle: string): { handle: string, url: string } | null {
        const normalizedHandle = iconHandle.trim().toLowerCase()
        for (const iconButton of this.iconButtons) {
            if (!item.contains(iconButton)) continue
            const buttonHandle = (iconButton.dataset.handle ?? '').trim().toLowerCase()
            if (buttonHandle !== normalizedHandle) continue

            return {
                handle: iconButton.dataset.handle?.trim() || iconHandle,
                url: iconButton.dataset.iconUrl || ''
            }
        }
        return null
    }

    private getPersonalizationValues(item: HTMLElement): string[] {
        const personalizationValues: string[] = []
        q.rl('.product-personalization__input-content', item).forEach(inputContent => {
            const input = q.os<HTMLInputElement>('input', inputContent)
            if (inputContent.dataset.icon) {
                personalizationValues.push(inputContent.dataset.icon as string)
            } else if (input?.value) {
                personalizationValues.push(input.value.toUpperCase())
            }
        })
        return personalizationValues
    }

    private clearPresetSelection(item: HTMLElement) {
        q.ol<HTMLButtonElement>('.product-personalization__preset-button', item)?.forEach(button => {
            button.classList.remove('is-active')
            button.removeAttribute('aria-pressed')
        })
    }

    private setActivePresetButton(item: HTMLElement, preset: string) {
        q.ol<HTMLButtonElement>('.product-personalization__preset-button', item)?.forEach(button => {
            const isActive = button.dataset.preset === preset
            button.classList.toggle('is-active', isActive)
            button.setAttribute('aria-pressed', String(isActive))
        })
    }

    applyPresetToItem(item: HTMLElement, preset: string) {
        const inputContents = Array.from(q.rl('.product-personalization__input-content', item))
        const presetTokens = preset.split('|').map(token => token.trim())

        inputContents.forEach((content, index) => {
            const token = presetTokens[index] || ''

            if (!token) {
                this.clearInputContent(content)
                return
            }

            if (token.length === 1) {
                this.setInputContentValue(content, token)
                return
            }

            const resolvedIcon = this.resolveIconSelection(item, token)
            this.setInputContentAsIcon(content, resolvedIcon?.handle || token, resolvedIcon?.url || '')
        })

        this.setActivePresetButton(item, preset)
        this.updateInputTextColors(item)
        if (item.classList.contains('active')) this.updateMediaBackgroundFromActive()
        this.hideCharacterError()
        this.onChangePersonalizationValue()
        this.validateInputs()
    }

    onChangePersonalizationValue() {
        q.rl('.product-personalization__item', this).forEach(wrapper => {
            const personalizationValues = this.getPersonalizationValues(wrapper)
            const colorInput = q.os<HTMLInputElement>('.product-personalization__color-input:checked', wrapper)
            const selectedValueEl = q.rs('.selected-value', wrapper)
            const hasPersonalization = personalizationValues.length > 0

            if (hasPersonalization && colorInput?.value) {
                personalizationValues.push(colorInput.value)
            }

            if (wrapper.dataset.option === 'right' && wrapper.hasAttribute('data-added')) {
                wrapper.setAttribute('data-added', hasPersonalization ? 'true' : 'false')
            }

            selectedValueEl.textContent = hasPersonalization ? personalizationValues.join(', ') : ''
        })
    }

    getSelectedColorHex(item: HTMLElement): string | null {
        const input = item.querySelector('.product-personalization__color-input:checked') as HTMLInputElement | null
        return input?.dataset.hex || null
    }

    updateInputTextColors(item: HTMLElement) {
        const monogramHex = this.getSelectedColorHex(item)
        if (!monogramHex) return
        const productHex = this.dataset.productColor || ''
        q.rl('.product-personalization__input-content', item).forEach(wrapper => {
            if (productHex) (wrapper as HTMLElement).style.backgroundColor = productHex
            const input = q.os<HTMLInputElement>('input', wrapper)
            if (input) {
                input.style.backgroundColor = 'transparent'
                input.style.color = monogramHex
            }
			const mask = (wrapper as HTMLElement).querySelector('[data-pp-icon-mask]') as HTMLElement | null
			if (mask) {
				mask.style.backgroundColor = monogramHex
			}
        })
    }

    updateMediaBackgroundFromActive() {
        const activeItem = this.querySelector('.product-personalization__item.active') as HTMLElement | null
        const media = this.querySelector('.product-personalization__media-image') as HTMLElement | null
        if (!activeItem || !media) return
        const hex = this.getSelectedColorHex(activeItem)
        if (!hex) return
        media.style.backgroundColor = hex
    }

    applyInitialColors() {
        const leftItem = this.querySelector('.product-personalization__item[data-option="left"]') as HTMLElement | null
        const rightItem = this.querySelector('.product-personalization__item[data-option="right"]') as HTMLElement | null
        if (leftItem) this.updateInputTextColors(leftItem)
        if (rightItem) this.updateInputTextColors(rightItem)
        this.updateMediaBackgroundFromActive()
    }

    private filterAllowedCharacters(input: string): string {
        return input.toUpperCase().split('').filter(char => ProductPersonalization.ALLOWED_CHARACTERS_PATTERN.test(char)).join('')
    }

    private showCharacterError() {
        if (!this.errorMessage) return

        this.errorMessage.textContent = 'Only uppercase letters (A-Z), numbers (0-9), and special characters (+, -, (, ), *, &, ^, %, $, #, @, !) are allowed.'
        this.errorMessage.hidden = false
        this.errorMessage.setAttribute('role', 'alert')

        if (this.characterErrorTimeout) {
            clearTimeout(this.characterErrorTimeout)
        }
        this.characterErrorTimeout = window.setTimeout(() => {
            this.hideCharacterError()
        }, 3000)
    }

    private hideCharacterError() {
        if (this.characterErrorTimeout) {
            clearTimeout(this.characterErrorTimeout)
            this.characterErrorTimeout = undefined
        }

        this.validateInputs()
    }

    onBeforeInputCharacter(e: Event) {
        const inputEvent = e as InputEvent
        if (!inputEvent.data) return
        
        const inputData = inputEvent.data.toUpperCase()
        
        if (!ProductPersonalization.ALLOWED_CHARACTERS_PATTERN_FULL.test(inputData)) {
            e.preventDefault()
            this.showCharacterError()
        }
    }

    onChangeCharacter(e: Event) {
        e.preventDefault()
        const wrapper = q.rClosestTarget(e, '.product-personalization__input-content')
        this.clearInputContent(wrapper)
		const target = e.currentTarget as HTMLInputElement
		if (target && target.value) {
            const originalValue = target.value
            const filteredValue = this.filterAllowedCharacters(target.value)
            target.value = filteredValue
            
            if (originalValue !== filteredValue && originalValue.length > filteredValue.length) {
                this.showCharacterError()
            } else {
                this.hideCharacterError()
            }
		} else {
            this.hideCharacterError()
        }
        const item = q.rc(wrapper, '.product-personalization__item')
        this.clearPresetSelection(item)
        this.onChangePersonalizationValue()
		if (target && target.value && target.value.length >= (target.maxLength || 1)) {
			this.focusNextInput(wrapper as HTMLElement)
		}
        this.validateInputs()
    }

    onKeyupCharater(e: KeyboardEvent) {
        const target = e.currentTarget as HTMLInputElement
        const wrapper = q.rClosestTarget(e, '.product-personalization__input-content')
        if(!wrapper || !target) return
        if(wrapper.dataset.icon && e.code?.toUpperCase() !== 'ESCAPE') {
            this.clearInputContent(wrapper)
            target.dispatchEvent(new Event('input', { bubbles: true }))
        }
        const item = q.rc(wrapper, '.product-personalization__item')
        this.clearPresetSelection(item)
        this.onChangePersonalizationValue()
        this.validateInputs()
    }

    toggleItem(e: Event) {
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        const addRightButton = q.rs('.button-add-right', this)
        const leftBackButton = q.rs('.button-back-left', this)
        if(target.classList.contains('button-add-right')) {
            this.items[0].classList.remove('active')
            this.items[1].classList.add('active')
            addRightButton.classList.add('hidden')
            leftBackButton.classList.remove('hidden')
        } else {
            this.items[0].classList.add('active')
            this.items[1].classList.remove('active')
            addRightButton.classList.remove('hidden')
            leftBackButton.classList.add('hidden')
        }
        this.updateMediaBackgroundFromActive()
        this.validateInputs()
    }

    onClickEditButton(e: Event) {
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        const wrapper = q.rc(target, '.product-personalization__item')
        this.items.forEach(item => {
            item.classList.toggle('active', wrapper === item)
        })
        this.updateMediaBackgroundFromActive()
        this.validateInputs()
    }

    onClickPresetButton(e: Event) {
        e.preventDefault()
        const target = e.currentTarget as HTMLButtonElement
        if (!target) return
        const item = q.rc(target, '.product-personalization__item')
        const preset = target.dataset.preset?.trim()
        if (!preset) return
        this.applyPresetToItem(item, preset)
    }

    onClickIconButton(e: Event) {
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        const img = q.rs('.media img', target) as HTMLImageElement
        const iconHandle = target.dataset.handle
        const wrapper = q.rc(target, '.product-personalization__input-field')
        if(!img || !iconHandle || !wrapper) return
        const item = q.rc(target, '.product-personalization__item') as HTMLElement | null
        if (item) this.clearPresetSelection(item)
		const inputWrappers = q.rl('.product-personalization__input-content', wrapper)
		let activeContent: HTMLElement | undefined
		inputWrappers.forEach(inputWrapper => {
			if(inputWrapper.classList.contains('active')) {
                this.setInputContentAsIcon(inputWrapper as HTMLElement, iconHandle, img.src)
				activeContent = inputWrapper as HTMLElement
			}
		})
		if (activeContent) this.focusNextInput(activeContent)
        this.onChangePersonalizationValue()
        this.validateInputs()
    }

    onClickBody(e: Event) {
        if (!this.classList.contains('active')) return
        const target = e.target as HTMLElement
        if(!target) return
        if(!target.closest('.add-icon')) {
            this.iconsPopups.forEach(popup => {
                popup.classList.remove('active')
            })
        }
        if(!target.closest('.product-personalization-content') && !target.closest('.trigger-personalization-popup') && !target.closest('cart-edit-button')) {
            this.closePopup()
        }
    }

    close(e: Event) {
        e.preventDefault()
        e.stopPropagation()
        this.closePopup()
    }

    formatCurrency(price: number) {
        const currency = this.submitButton.dataset.currency || 'USD'
        const local = this.submitButton.dataset.local || 'en-US'
        return new Intl.NumberFormat(local, { style: "currency", currency: currency }).format(price)
    }

    onChangeColor(e: Event) {
        const target = e.currentTarget as HTMLInputElement
        if(!target) return
        const item = target.closest('.product-personalization__item') as HTMLElement | null
        const selectedColorEl = item?.querySelector('.product-personalization__selected-color')
        const color = target.value
        if(selectedColorEl && color) selectedColorEl.textContent = color.trim()
        if (item) {
            this.updateInputTextColors(item)
            if (item.classList.contains('active')) this.updateMediaBackgroundFromActive()
            // If preview step is active, update preview colors to reflect new selection
            if (this.editItems[1] && this.editItems[1].classList.contains('active')) {
                const preview = q.os<HTMLElement>('.product-personalization__note-preview', this)
                if (preview) {
                    const productHex = this.dataset.productColor || ''
                    if (productHex) preview.style.backgroundColor = productHex
                    const monogramHex = this.getSelectedColorHex(item)
                    if (monogramHex) preview.style.color = monogramHex
                }
            }
        }
        this.onChangePersonalizationValue()
    }

    next(e: Event) {
        e.preventDefault()
        if (!this.validateInputs()) return
        this.editItems[0].classList.remove('active')
        this.editItems[1].classList.add('active')
        const price = this.formatCurrency((Number(this.dataset.price) +  Number(this.dataset.variantPrice)) / 100)
        const priceEl = q.rs('.product-personalization__submit-price', this.submitButton)
        priceEl.textContent = price
        const personalizationPreview = q.rs('.product-personalization__note-preview', this)
        const activeItem = this.querySelector('.product-personalization__item.active') as HTMLElement | null
        if (!activeItem) return
        const monogramHex = activeItem ? this.getSelectedColorHex(activeItem) : null
        const productHex = this.dataset.productColor || ''
        if (productHex) (personalizationPreview as HTMLElement).style.backgroundColor = productHex
        if (monogramHex) (personalizationPreview as HTMLElement).style.color = monogramHex
        personalizationPreview.innerHTML = ''

        const inputWrapper = q.os<HTMLElement>('.product-personalization__input-wrapper', activeItem)
        if (!inputWrapper) return
        q.rl('.product-personalization__input-content', inputWrapper).forEach(content => {
            const input = q.rs('.character-input', content) as HTMLInputElement
            if(!input) return
            if(content.dataset.icon) {
				const url = (content as HTMLElement).dataset.iconUrl || ''
				if (url) {
					const mask = this.createIconMaskElement(url, monogramHex || '#000')
					personalizationPreview.appendChild(mask)
				}
            } else if (input.value) {
                const span = document.createElement('span')
				span.classList.add('h1')
				span.style.color = monogramHex || ''
                span.textContent = input.value.toUpperCase()
                personalizationPreview.appendChild(span)
            }
        })
    }

    previous(e: Event) {
        e.preventDefault()
        this.editItems[0].classList.add('active')
        this.editItems[1].classList.remove('active')
    }

    validateFinalSaleAcknowledgement(): boolean {
        if (this.isEditMode()) return true
        if (!this.finalSaleCheckbox) return true

        const isChecked = this.finalSaleCheckbox.checked
        if (this.finalSaleError) {
            this.finalSaleError.hidden = isChecked
        }

        return isChecked
    }

    getSpinner() {
		return q.rs(SELECTORS.loadingOverlaySpinner, this.submitButton)
	}

    reset() {
        // Toggle edit sections
        this.editItems?.[0]?.classList.add('active')
        this.editItems?.[1]?.classList.remove('active')
        this.items.forEach(item => {
            this.clearPresetSelection(item)
        })

        // Clear input values
        this.characterInputs?.forEach(input => {
            const wrapper = input.closest('.product-personalization__input-content') as HTMLElement
            wrapper.classList.remove('active')
            this.clearInputContent(wrapper)
        })

        // Clear error timeout and hide error message
        this.hideCharacterError()
        if (this.finalSaleCheckbox) this.finalSaleCheckbox.checked = false
        if (this.finalSaleError) this.finalSaleError.hidden = true

        // Reset color selections
        const leftColor = this.querySelector('[name="left-color"]') as HTMLInputElement | null
        const rightColor = this.querySelector('[name="right-color"]') as HTMLInputElement | null
        if (leftColor) leftColor.checked = true
        if (rightColor) rightColor.checked = true

        // Reset selected values
        this.querySelectorAll('.selected-value').forEach(el => el.textContent = '')

        // Toggle product items
        const items = this.querySelectorAll('.product-personalization__item')
        items[0]?.classList.add('active')
        items[1]?.classList.remove('active')

        // Mark as added
        const AddedItem = this.querySelector('.product-personalization__item[data-added="true"]')
        if (AddedItem) AddedItem.setAttribute('data-added', 'false')

        if (this.addRightButton) this.addRightButton.classList.remove('hidden')
        if (this.leftBackButton) this.leftBackButton.classList.add('hidden')
        this.applyInitialColors()
        this.validateInputs()
    }

    onSubmitHandler(e: Event) {
        e.preventDefault()
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return
        if (!this.validateFinalSaleAcknowledgement()) return
        
        this.submitButton.setAttribute('aria-disabled', 'true')
        this.submitButton.setAttribute(ATTRIBUTES.loading, '')
        this.getSpinner().classList.remove('hidden')

        const formData = new FormData(this.form)
        const formVariantId = formData.get('items[0][id]')
        if (!formVariantId) throw Error('No variant id found')
        const addedVariantId = parseInt(formVariantId.toString())
        const personalizationProperties = this.buildPersonalizationProperties()

        if (this.isEditMode()) {
            const line = this.dataset.editLine
            if (!line) throw Error('No cart line found for edit mode')

            const body = JSON.stringify({
                line,
                quantity: 1,
                properties: personalizationProperties,
                sections: getDOMCartSectionApiIds(),
                sections_url: window.location.pathname,
            })

            fetch(`${window.routes.cart_change_url}`, {
                ...fetchConfig(),
                ...{ body },
            })
                .then((response) => response.json())
                .then((cart) => {
                    renderResponseToCartDrawer(cart)
                })
                .catch((error) => {
                    console.error(error)
                })
                .finally(() => {
                    this.submitButton.removeAttribute(ATTRIBUTES.loading)
                    if (!this.error) this.submitButton.removeAttribute('aria-disabled')
                    this.getSpinner().classList.add('hidden')
                    this.classList.remove('active')
                })

            return
        }

        Object.entries(personalizationProperties).forEach(([key, value]) => {
            formData.set(`items[1][properties[${key}]]`, value)
        })

        type AddToCartItem = {
            id: number
            quantity: number
            selling_plan?: number
            properties: Record<string, string>
            parent_id?: number
        }

        const items = createATCitems(formData) as AddToCartItem[]
		const cartAttributes = getCartAttributesFromFormData(formData)

        addItemsToCart(items, getDOMCartSectionApiIds(), cartAttributes)
            .then((cart) => {
                if ('status' in cart) {
                    return
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: addedVariantId,
                })
                renderResponseToCartDrawer(cart)
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                this.submitButton.removeAttribute(ATTRIBUTES.loading)

                if (!this.error)
                    this.submitButton.removeAttribute('aria-disabled')
                this.getSpinner().classList.add('hidden')
                this.reset()
                this.classList.remove('active')
            })
    }
}
